/**
 * Auto — /tools/auto
 *
 * Realm-X-style copilot toolkit. Three interactive demos, all backed
 * by /api/auto/*:
 *   • Contextual Action Chips — feed a JSON context blob (or pick a
 *     preset) and get 3-5 one-tap next-best-actions.
 *   • Document Parser — paste lease/receipt/notice text; get
 *     structured JSON (dates, amounts, parties, clauses).
 *   • Correctness Checker — paste any JSON payload; get a list of
 *     issues + suggested fixes (Ontario RTA § 106 etc.).
 *
 * The <AutoChips context={…}/> component below is the same primitive
 * this page uses — drop it on any page (Dashboard, Tenant Detail,
 * Lease Detail) and Auto shows up in-context.
 */
import { useMemo, useState } from "react";
import axios from "axios";
import {
  Sparkles, Zap, Loader2, AlertCircle, Check, X, FileText, Wand2,
  ClipboardList, ShieldCheck,
} from "lucide-react";
import { usePageTitle, useNoIndex } from "@/lib/seo";
import AiLoader from "@/components/AiLoader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -----------------------------------------------------------------------------
// Reusable AutoChips primitive — drop it on any screen with context + entity.
// -----------------------------------------------------------------------------
export function AutoChips({ context, entity, goal, onAction, className = "" }) {
  const [chips, setChips] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auto/chips`, { context: context || {}, entity: entity || "tenant", goal });
      setChips(data.chips || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Auto could not load chips.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`p-3 rounded-xl border border-emerald-200 bg-emerald-50/40 ${className}`} data-testid="auto-chips">
      <div className="flex items-center gap-2 mb-2">
        <Wand2 className="h-4 w-4 text-emerald-700" />
        <span className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold">Auto — next best actions</span>
      </div>
      {!chips && !busy && (
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition"
          data-testid="auto-chips-load"
        >
          <Sparkles className="h-3.5 w-3.5" /> Suggest actions
        </button>
      )}
      <AiLoader
        busy={busy}
        variant="inline"
        label="Auto is thinking"
        stages={[
          "Reading this screen's context…",
          "Weighing possible actions…",
          "Filtering by provincial RTA…",
          "Ranking by priority…",
        ]}
        testid="auto-chips-loader"
      />
      {error && <div className="text-xs text-rose-700">{error}</div>}
      {chips && chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((c, i) => (
            <button
              key={i}
              onClick={() => onAction?.(c)}
              title={c.reason}
              data-testid={`auto-chip-${i}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                c.priority === "urgent"
                  ? "bg-rose-600 text-white hover:bg-rose-700"
                  : c.priority === "low"
                    ? "bg-white border border-slate-300 text-slate-700 hover:border-emerald-500"
                    : "bg-emerald-600 text-white hover:bg-emerald-700"
              }`}
            >
              {c.priority === "urgent" && <AlertCircle className="h-3 w-3" />}
              {c.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Demo page — /tools/auto
// -----------------------------------------------------------------------------
const CHIP_PRESETS = {
  tenant_late: {
    entity: "tenant",
    goal: "get rent paid this week without escalating",
    context: {
      tenant_name: "Amanda R.",
      unit: "4B",
      province: "ON",
      rent_cad: 2100,
      days_late: 6,
      last_payment: "2025-11-02",
      history: "1 previous late payment in 12 months",
    },
  },
  lease_ready_to_sign: {
    entity: "lease",
    goal: "finalize the lease with the applicant",
    context: {
      applicant_name: "Marcel D.",
      unit: "12S",
      province: "QC",
      start_date: "2026-03-01",
      rent_cad: 1850,
      credit_score: 742,
      background_check: "clear",
    },
  },
  invoice_reconcile: {
    entity: "invoice",
    goal: "clear this invoice from the ledger",
    context: {
      invoice_id: "INV-2140",
      amount_cad: 1875,
      status: "matched_by_reconciler",
      bank_ref: "Stripe payout 2025-11-15",
    },
  },
};

const PARSE_PRESET =
`RESIDENTIAL LEASE AGREEMENT
Between: Amanda Reid ("Tenant") and 1234567 Ontario Ltd. ("Landlord")

Rental Unit: 4B, 88 Yonge St, Toronto, ON M5E 1J8
Term: December 1, 2025 to November 30, 2026
Monthly Rent: $2,100 CAD due on the 1st of each month
Last month's rent deposit: $2,100 CAD paid on signing
Signed: November 12, 2025

Clause 12 (No Pets): The Tenant shall not keep pets of any kind on the premises.
Clause 14 (Utilities): The Tenant shall pay all utilities except water.`;

const CORRECT_PRESET = {
  schema_hint: "lease",
  province: "ON",
  payload: {
    tenant: "Amanda Reid",
    landlord: "1234567 Ontario Ltd.",
    unit: "4B",
    start_date: "2025-12-01",
    end_date: "2026-11-30",
    rent: 2100,
    deposit: 4200,
    postal: "M5E1J8",
    late_fee_pct: 25,
  },
};

export default function Auto() {
  useNoIndex();
  usePageTitle(
    "Portfolio copilot — Central Rentals",
    "Subscriber tool: a drop-in copilot for Canadian landlords and property managers. Action chips on every page, intelligent document parsing and RTA-correctness checks — turn admin hours into new-lead time.",
    "portfolio copilot, landlord copilot, property management copilot, RTA compliance checker, document parser, Central Rentals",
  );
  const [tab, setTab] = useState("chips");
  return (
    <div className="max-w-5xl mx-auto px-6 py-14 sm:py-20" data-testid="auto-page">
      <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-3">Lead generation · Subscribers only</p>
      <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-6">Free up hours to chase leads — let the copilot handle the paperwork.</h1>
      <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mb-10">
        Every corner of your portal gets a copilot layer that parses
        documents, drafts responses and flags compliance issues before
        you send. Landlords and property-management companies use it to
        cut admin from hours to minutes — and turn that time into
        prospecting, showings and new signed doors.
      </p>

      <div className="mb-6 flex flex-wrap gap-2" data-testid="auto-tabs">
        {[
          { k: "chips",   Icon: Wand2,         label: "Action Chips" },
          { k: "parse",   Icon: FileText,      label: "Document Parser" },
          { k: "correct", Icon: ShieldCheck,   label: "Correctness" },
        ].map(({ k, Icon, label }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`auto-tab-${k}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${
              tab === k
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-300 text-slate-700 hover:border-emerald-500 hover:text-emerald-700"
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "chips" && <ChipsDemo />}
      {tab === "parse" && <ParseDemo />}
      {tab === "correct" && <CorrectDemo />}
    </div>
  );
}

function ChipsDemo() {
  const [preset, setPreset] = useState("tenant_late");
  const active = CHIP_PRESETS[preset];
  const [key, setKey] = useState(0);
  return (
    <section className="p-6 rounded-2xl border border-slate-200 bg-white" data-testid="auto-chips-demo">
      <div className="flex items-center gap-3 mb-4">
        <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
          <Wand2 className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">Contextual action chips</h2>
          <p className="text-sm text-slate-500">Auto reads the current screen and suggests the next 3-5 actions.</p>
        </div>
      </div>
      <div className="mb-4 flex flex-wrap gap-2">
        {Object.entries(CHIP_PRESETS).map(([k, p]) => (
          <button
            key={k}
            onClick={() => { setPreset(k); setKey((n) => n + 1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
              preset === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:border-emerald-500"
            }`}
            data-testid={`chip-preset-${k}`}
          >
            {p.entity} · {p.context.province || "—"}
          </button>
        ))}
      </div>
      <pre className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap font-mono bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4 overflow-x-auto">
{JSON.stringify(active.context, null, 2)}
      </pre>
      <AutoChips
        key={key}  /* Force fresh mount on preset switch */
        entity={active.entity}
        goal={active.goal}
        context={active.context}
        onAction={(c) => alert(`Action fired: ${c.action}\n\nPayload: ${JSON.stringify(c.payload)}`)}
      />
    </section>
  );
}

function ParseDemo() {
  const [text, setText] = useState(PARSE_PRESET);
  const [kind, setKind] = useState("lease");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/auto/parse`, { text, kind });
      setResult(data);
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || "Parse failed." });
    } finally { setBusy(false); }
  };
  return (
    <section className="p-6 rounded-2xl border border-slate-200 bg-white" data-testid="auto-parse-demo">
      <div className="flex items-center gap-3 mb-4">
        <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
          <FileText className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">Intelligent document parser</h2>
          <p className="text-sm text-slate-500">Paste any lease, receipt, notice or invoice. Get structured JSON.</p>
        </div>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Kind</label>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          data-testid="parse-kind"
          className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm bg-white"
        >
          {["lease","receipt","notice","invoice","application","other"].map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <button
          onClick={run}
          disabled={busy || !text.trim()}
          data-testid="parse-run"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
        >
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Parsing…</> : <><Sparkles className="h-4 w-4" /> Parse</>}
        </button>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={10}
        data-testid="parse-text"
        className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm font-mono"
      />
      {result?.error && <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">{result.error}</div>}
      {result && !result.error && (
        <div className="mt-4" data-testid="parse-result">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
            Extracted (source: {result.source})
          </div>
          <pre className="text-xs text-slate-800 leading-relaxed whitespace-pre-wrap font-mono bg-slate-50 p-4 rounded-lg border border-slate-200 overflow-x-auto">
{JSON.stringify(result.result, null, 2)}
          </pre>
        </div>
      )}
    </section>
  );
}

function CorrectDemo() {
  const [text, setText] = useState(JSON.stringify(CORRECT_PRESET, null, 2));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const parsed = useMemo(() => { try { return JSON.parse(text); } catch { return null; } }, [text]);

  const run = async () => {
    if (!parsed) { setResult({ error: "Invalid JSON — fix the syntax and try again." }); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/auto/correctness`, parsed);
      setResult(data);
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || "Check failed." });
    } finally { setBusy(false); }
  };

  return (
    <section className="p-6 rounded-2xl border border-slate-200 bg-white" data-testid="auto-correct-demo">
      <div className="flex items-center gap-3 mb-4">
        <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">Correctness checker</h2>
          <p className="text-sm text-slate-500">RTA §-cited findings + inline fixes for any lease/invoice/notice JSON.</p>
        </div>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={10}
        data-testid="correct-text"
        className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm font-mono"
      />
      <div className="mt-3">
        <button
          onClick={run}
          disabled={busy || !parsed}
          data-testid="correct-run"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
        >
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking…</> : <><ShieldCheck className="h-4 w-4" /> Run check</>}
        </button>
      </div>
      {result?.error && <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">{result.error}</div>}
      {result && !result.error && (
        <div className="mt-4" data-testid="correct-result">
          <div className="flex flex-wrap items-center gap-3 mb-3 text-xs">
            <span className="px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-800 font-bold">
              {result.errors} error{result.errors === 1 ? "" : "s"}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 font-bold">
              {result.warnings} warning{result.warnings === 1 ? "" : "s"}
            </span>
            <span className="text-slate-500">{result.summary}</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {(result.issues || []).map((i, idx) => (
              <li key={idx} className="py-3 flex items-start gap-3" data-testid={`correct-issue-${idx}`}>
                <div className={`shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center text-white ${
                  i.severity === "error" ? "bg-rose-600" : i.severity === "warning" ? "bg-amber-500" : "bg-slate-400"
                }`}>
                  {i.severity === "error" ? <X className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{i.field || "(payload)"}: {i.message}</div>
                  {i.suggested_fix && (
                    <div className="text-xs text-emerald-800 mt-1 p-2 rounded-lg bg-emerald-50 border border-emerald-100">
                      <span className="font-semibold">Fix: </span>{i.suggested_fix}
                    </div>
                  )}
                  {i.statute && (
                    <div className="text-[11px] text-slate-500 mt-1 italic">Statute: {i.statute}</div>
                  )}
                </div>
              </li>
            ))}
            {(!result.issues || result.issues.length === 0) && (
              <li className="py-6 text-center text-sm text-emerald-700 flex items-center justify-center gap-2">
                <Check className="h-4 w-4" /> All checks passed.
              </li>
            )}
          </ul>
        </div>
      )}
    </section>
  );
}
